import { SendOutlined } from '@ant-design/icons';
import {
  getGetFrontRichiesteIdComunicazioniQueryKey,
  usePostFrontComunicazioniInviaComunicazioneId,
  usePostFrontRichiesteIdComunicazioniCreaRisposta,
  type ComunicazioneDTO,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  CommunicationLogic,
  ReplyCommunicationRequest,
  ReplyCommunicationRequestSchema,
} from '@framework/common-web';
import {
  FormHandler,
  FormItem,
  SubmitButton,
  TextAreaInput,
} from '@framework/form';
import { useTranslation } from '@framework/i18n';
import { AsyncButton, Drawer, DrawerButtonRow, FlexRow } from '@framework/ui';
import { Button, message, Space } from 'antd';
import { useMemo, useState } from 'react';
import { queryClient } from '../../../core/network/queryClient';

export interface ISubmissionReplyCommunicationDrawerProps {
  submissionId?: string | null;
  /** Comunicazione ricevuta a cui si sta rispondendo */
  communication: ComunicazioneDTO | null;
  onClose: () => void;
}

/**
 * Drawer per rispondere a una comunicazione ricevuta da un endoprocedimento.
 * Flusso in due step: SALVA crea la risposta (`crea-risposta`), poi INVIA la
 * invia riusando il servizio standard di invio.
 */
export function SubmissionReplyCommunicationDrawer(
  props: ISubmissionReplyCommunicationDrawerProps,
) {
  const { submissionId, communication, onClose } = props;
  const { t } = useTranslation();

  const [createdReplyId, setCreatedReplyId] = useState<string | null>(null);

  const initialValues = useMemo(
    () => ReplyCommunicationRequestSchema.cast({}, { assert: false }),
    [],
  );

  const open = communication != null;
  const recipientName = CommunicationLogic.sender(communication)?.denominazione;

  const createReplyMutation =
    usePostFrontRichiesteIdComunicazioniCreaRisposta();
  const sendMutation = usePostFrontComunicazioniInviaComunicazioneId();

  const handleClose = () => {
    setCreatedReplyId(null);
    onClose();
  };

  const handleSave = async (data: ReplyCommunicationRequest) => {
    if (!communication?.id || !submissionId) return;
    try {
      const created = await createReplyMutation.mutateAsync({
        id: submissionId,
        data: {
          idComunicazione: communication.id,
          messaggio: data.messaggio,
        },
      });

      if (!created.id) {
        throw new Error(
          "La risposta è stata creata ma il backend non ha restituito l'id",
        );
      }

      setCreatedReplyId(created.id);
      // La bozza appena creata deve comparire nella lista anche se l'utente
      // chiude senza inviare: senza questa invalidazione la lista resterebbe
      // stale fino al prossimo refetch (es. invio).
      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdComunicazioniQueryKey(submissionId),
      );
      message.success(
        t('reply-communication.created', 'Risposta creata con successo'),
      );
    } catch (e) {
      message.error(
        getNetworkErrorMessage(
          e,
          t(
            'reply-communication.create-error',
            'Impossibile creare la risposta',
          ),
        ),
      );
      console.error(e);
    }
  };

  const handleSend = async () => {
    if (!createdReplyId) return;
    try {
      await sendMutation.mutateAsync({
        idIstanzaComunicazione: createdReplyId,
      });
      await queryClient.invalidateQueries(
        getGetFrontRichiesteIdComunicazioniQueryKey(submissionId ?? ''),
      );
      message.success(
        t(
          'communications.sent-successfully',
          'Comunicazione inviata con successo',
        ),
      );
      handleClose();
    } catch (e) {
      message.error(
        getNetworkErrorMessage(
          e,
          t(
            'communications.error-sending-communication',
            "Si è verificato un errore durante l'invio della comunicazione",
          ),
        ),
      );
      console.error(e);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      destroyOnClose
      maskClosable={false}
      title={t('reply-communication.title', 'Rispondi alla comunicazione')}
      size="large"
      placement="right"
    >
      <FormHandler
        id="reply-communication"
        layout="vertical"
        schema={ReplyCommunicationRequestSchema}
        initialValues={initialValues}
        onSubmit={handleSave}
      >
        <FlexRow>
          <Space>
            <span>{t('reply-communication.recipient', 'Destinatario')}:</span>
            <strong>{recipientName}</strong>
          </Space>
        </FlexRow>
        <FormItem
          name="messaggio"
          label={t('communications.message', 'Messaggio')}
        >
          <TextAreaInput
            name="messaggio"
            rows={6}
            disabled={createdReplyId != null}
            placeholder={t(
              'communications.message-placeholder',
              'Messaggio della comunicazione',
            )}
          />
        </FormItem>
        <DrawerButtonRow>
          <Space>
            <Button size="large" onClick={handleClose}>
              {t('common.annulla', 'Annulla')}
            </Button>
            <SubmitButton
              size="large"
              type="primary"
              disabled={createdReplyId != null || undefined}
            />
            <AsyncButton
              size="large"
              type="primary"
              icon={<SendOutlined aria-hidden />}
              disabled={createdReplyId == null}
              onClick={handleSend}
            >
              {t('comunicazioni.invia', 'Invia')}
            </AsyncButton>
          </Space>
        </DrawerButtonRow>
      </FormHandler>
    </Drawer>
  );
}
